import { useMemo } from 'react'

import { formatDate } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import {
  Checkbox, StatusBadge, Table, Text,
} from '@domain-group/suit-theme'

import { getAdQueueStartDate } from '@functions/adQueue'
import { FACEBOOK_AD_TYPE, GOOGLE_DISPLAY_AD_TYPE, GOOGLE_SEARCH_AD_TYPE } from '@functions/adType'
import getStatusBadgeProps from '@functions/statusBadge'
import camelCaseToTitleCase from '@functions/utils/camelCaseToTitleCase'

import useSelector from '@hooks/useSelector'

import { AdQueueModel } from '@models/types'

import type { EntitiesState } from '@redux/entities'

import Actions from './Actions'

type BuildColumnsParams = {
  entities: EntitiesState,
  hasSelectedAllAdQueues: boolean,
  selectedAdQueueIds: number[],
  toggleAllAdQueues: () => void,
  toggleSelectedAdQueueId: (id: number) => number[],
}

const buildColumns = (params: BuildColumnsParams) => {
  const {
    entities,
    hasSelectedAllAdQueues,
    selectedAdQueueIds,
    toggleAllAdQueues,
    toggleSelectedAdQueueId,
  } = params

  const { campaigns, facebookCampaigns, pendingGoogleCampaigns } = entities

  return [
    {
      title: (
        <Checkbox
          checked={hasSelectedAllAdQueues}
          marginRight={0}
          onClick={() => toggleAllAdQueues()}
        />
      ),
      dataKey: 'checkbox',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      render: (_: any, adQueue: AdQueueModel) => {
        const { id } = adQueue

        return (
          <Checkbox
            checked={selectedAdQueueIds.includes(id)}
            marginRight={0}
            onClick={() => toggleSelectedAdQueueId(id)}
          />
        )
      },
    },
    {
      title: 'Campaign',
      dataKey: 'campaignName',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (_: any, adQueue: AdQueueModel) => {
        const { campaignId } = adQueue
        const campaign = campaigns[campaignId]
        const { name } = campaign || {}

        if (name) {
          return (
            <Text variant="ellipsis">
              {name}
            </Text>
          )
        }

        const { adPayload } = adQueue
        const { 
          name : lowerCaseName,
          Name : upperCaseName, 
        } = JSON.parse(adPayload)

        return (
          <Text variant="ellipsis">
            {upperCaseName ?? lowerCaseName}
          </Text>
        )
      },
    },
    {
      title: 'AIM#',
      dataKey: 'campaignId',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 40,
      },
      render: (_: any, adQueue: AdQueueModel) => {
        const { 
          campaignId,
          referenceId
        } = adQueue

        return (
          <Text variant="ellipsis">
            {campaignId ?? referenceId}
          </Text>
        )
      },
    },
    {
      title: 'Deployment Stack',
      dataKey: 'deploymentStack',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      render: (_: any, adQueueModel: AdQueueModel) => (
        <Text variant="ellipsis">
          {adQueueModel.campaignId ? 'V2' : 'V1'}
        </Text>
      ),
    },
    {
      title: 'Due',
      dataKey: 'campaignRequiredAt',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 40,
      },
      render: (_: any, adQueue: AdQueueModel) => {
        const { campaignId } = adQueue
        const campaign = campaigns[campaignId]
        const { requiredAt } = campaign || {}

        if (requiredAt) {
          return (
            <StatusBadge
              boxProps={{
                backgroundColor: 'backgroundColor',
                borderColor: 'lineColor',
                color: 'black',
              }}
              text={formatDate(requiredAt, 'ISO8601', 'dd/MM/yyyy')}
            />
          )
        }

        const startDate = getAdQueueStartDate(adQueue)

        return (
          <StatusBadge
            boxProps={{
              backgroundColor: 'backgroundColor',
              borderColor: 'lineColor',
              color: 'black',
            }}
            text={formatDate(startDate, 'ISO8601', 'dd/MM/yyyy')}
          />
        )
      },
    },
    {
      title: 'Channel',
      dataKey: 'channel',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 40,
      },
      render: (_: any, adQueue: AdQueueModel) => {
        const { adTypeId } = adQueue

        return (
          <Text variant="ellipsis">
            {camelCaseToTitleCase(adTypeId)}
          </Text>
        )
      },
    },
    {
      title: 'Ad Account',
      dataKey: 'adAccount',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 40,
      },
      render: (_: any, adQueue: AdQueueModel) => {
        const { adTypeId, facebookCampaignId, pendingGoogleCampaignId } = adQueue || {}

        let adAccountId: number | undefined

        if (adTypeId === FACEBOOK_AD_TYPE) {
          adAccountId = facebookCampaignId ? facebookCampaigns?.[facebookCampaignId]?.facebookAdAccountId : undefined
        } else if (adTypeId === GOOGLE_DISPLAY_AD_TYPE || adTypeId === GOOGLE_SEARCH_AD_TYPE) {
          adAccountId = pendingGoogleCampaignId ? pendingGoogleCampaigns?.[pendingGoogleCampaignId]?.googleAccountId : undefined
        }

        return (
          <Text variant="ellipsis">
            {adAccountId}
          </Text>
        )
      },
    },
    {
      title: 'Error',
      dataKey: 'error',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        maxWidth: 240,
      },
      render: (_: any, adQueue: AdQueueModel) => {
        const { errorMessage } = adQueue

        return (
          <Text variant="ellipsis">
            {errorMessage}
          </Text>
        )
      },
    },
    {
      title: 'Status',
      dataKey: 'status',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 40,
      },
      render: (_: any, adQueue: AdQueueModel) => {
        const { status } = adQueue

        const statusBadgeProps = getStatusBadgeProps(status)

        return (
          <StatusBadge
            boxProps={{ borderWidth: 2 }}
            ghost
            {...statusBadgeProps}
          />
        )
      },
    },
    {
      title: 'Actions',
      dataKey: 'id',
      cellProps: {
        key: 'actions',
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 20,
      },
      render: (adQueueId: number) => <Actions adQueueId={adQueueId} />,
    },
  ]
}

const watchEntityKeys = ['adQueues', 'campaigns']

interface FailedCampaignsTableProps {
  adQueues: AdQueueModel[],
  callbacks: {
    toggleAllAdQueues: () => void,
    toggleSelectedAdQueueId: (id: number) => number[],
  },
  hasSelectedAllAdQueues: boolean,
  selectedAdQueueIds: number[],
}

const FailedCampaignsTable = (props: FailedCampaignsTableProps) => {
  const {
    adQueues,
    callbacks: {
      toggleAllAdQueues,
      toggleSelectedAdQueueId,
    },
    hasSelectedAllAdQueues,
    selectedAdQueueIds,
  } = props

  const {
    updatedEntities: {
      adQueues: adQueuesUpdatedAt,
      campaigns: campaignsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entities = useSelector(reduxState => reduxState.entities)

  const memoColumns = useMemo(() => (
    buildColumns({ entities, hasSelectedAllAdQueues, selectedAdQueueIds, toggleAllAdQueues, toggleSelectedAdQueueId })
  ), [adQueuesUpdatedAt, campaignsUpdatedAt, selectedAdQueueIds])

  return (
    <Table
      boxProps={{ border: 'none', maxHeight: 1600, overflowY: 'auto' }}
      columns={memoColumns}
      data={adQueues}
      scroll={{ x: 800 }}
      stickyHeader
    />
  )
}

export default FailedCampaignsTable
